import * as stylex from "@stylexjs/stylex";
import { TOCContext } from "components/hero-vis/TOC";
import useEmblaCarousel from "embla-carousel-react";
import { getFocusMetadata } from "focus-content";
import { useMedia, useRTL } from "hooks";
import useLocale from "hooks/useLocale";
import { ArrowLeft14 } from "icons/ArrowLeft";
import Link from "next/link";
import { O, pipe } from "prelude";
import React, { useCallback, useContext, useEffect } from "react";
import { defineMessage, useIntl } from "react-intl";
import { findArticleAlias, findArticleThemeAlias, getAliasHref, subthemesIndex, useAlias } from "routes";
import { focusArticles } from "../../focus";
import { breakpoints, colors, layoutAreas } from "../../materials/consts.stylex";
import { typography } from "../../materials/typography.stylex";
const styles = {
    header: {
        keoZOQ: "xotjm27",
        $$css: true
    },
    cardText: {
        kzqmXN: "xh8yej3",
        kVQacm: "xb3r6kr",
        k1xSpc: "x104kibb",
        kJFfOR: "x1q359dk",
        kgKLqz: "x1ua5tub",
        $$css: true
    },
    cardLink: {
        kMwMTN: "x12klvby",
        kDPRdz: "xhr9w20",
        k67Jb4: "xt0b8zv",
        $$css: true
    }
};
export function RelatedFocus() {
    const locale = useLocale();
    const alias = useAlias();
    const isRTL = useRTL();
    const screenMDown = !useMedia(breakpoints.m);
    const screenLDown = !useMedia(breakpoints.l);
    const screenLUp = useMedia(breakpoints.l);
    const intl = useIntl();
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: false,
        align: "start",
        containScroll: "trimSnaps",
        direction: isRTL ? "rtl" : "ltr"
    });
    const scrollPrev = useCallback(()=>{
        if (emblaApi) emblaApi.scrollPrev();
    }, [
        emblaApi
    ]);
    const scrollNext = useCallback(()=>{
        if (emblaApi) emblaApi.scrollNext();
    }, [
        emblaApi
    ]);
    const article = pipe(alias, findArticleAlias);
    const theme = pipe(article, O.map((article)=>findArticleThemeAlias(article)));
    const posts = pipe(theme, O.map((themeAlias)=>{
        const subthemes = subthemesIndex[themeAlias];
        return focusArticles.published.map((focus)=>getFocusMetadata(focus, locale)).filter((focus)=>subthemes.includes(focus.article)).sort((a, b)=>a.year - b.year);
    }), O.getOrElseW(()=>[]));
    const slideCount = screenMDown ? Math.min(posts.length, 1) : screenLDown ? Math.min(posts.length, 2) : Math.min(posts.length, 3);
    const onSelect = useCallback(()=>{
        if (!emblaApi) return;
        setCanScrollPrev(emblaApi.canScrollPrev());
        setCanScrollNext(emblaApi.canScrollNext());
    }, [
        emblaApi
    ]);
    useEffect(()=>{
        if (!emblaApi) return;
        emblaApi.on("select", onSelect);
        onSelect();
        return ()=>{
            emblaApi.off("select", onSelect);
        };
    }, [
        emblaApi,
        onSelect
    ]);
    const headerId = "related-focus-title";
    const { addId, removeId } = useContext(TOCContext);
    // addId and removeId do not have a stable reference, so they can not be
    // deps of this useEffect!
    const hasPosts = posts.length > 0;
    React.useEffect(()=>{
        if (hasPosts) {
            addId(headerId);
            return ()=>{
                removeId(headerId);
            };
        }
        return;
    }, [
        headerId,
        hasPosts
    ]);
    if (!hasPosts) {
        return null;
    }
    return <>
      <h2 id={headerId} {...stylex.props(typography.heading2, styles.header)}>
        {intl.formatMessage(defineMessage({
        id: "Related Focus Articles",
        defaultMessage: "Related Focus Articles"
    }))}
      </h2>
      <div {...{
        0: {
            className: "x6s0dn4 x78zum5 xgpatz3 x1bd1j73 xki0xp4"
        },
        1: {
            className: "x6s0dn4 x78zum5 xgpatz3 x1bd1j73 x1uq3dmu"
        }
    }[!!(screenLUp && slideCount < 3) << 0]}>
        <Carousel>
          {posts.length > slideCount && <CarouselButton onClick={scrollPrev} direction="previous" disabled={!canScrollPrev}/>}
          <CarouselViewport ref={emblaRef}>
            <CarouselContainer>
              {posts.map((article, i)=>{
        return <div key={i} className={`embla__slide ${({
            className: "xur7f20 x1barp0i xkib98w xggk2y7 x78zum5 xdt5ytf x1qughib xdduh5l x1u2d2a2 xh8yej3 x1qkgehf xpxr3h5"
        }).className}`} style={{
            flex: `0 0 ${95 / slideCount}%`
        }}>
                    <div>
                      <h3 {...stylex.props(typography.heading3)}>{article.title}</h3>
                      <hr className="x1exxlbk xc342km xjm9jq1 x1nbwhqy xrgqbfd"/>
                      <p {...stylex.props(typography.table, styles.cardText)}>{article.lead}</p>
                    </div>

                    <div className="x1hr2gdg xqui205">
                      <Link href={getAliasHref(article.alias)} {...stylex.props(typography.button, styles.cardLink)}>
                        {intl.formatMessage(defineMessage({
            id: "Read",
            defaultMessage: "Read"
        }))}
                      </Link>
                    </div>
                  </div>;
    })}
            </CarouselContainer>
          </CarouselViewport>
          {posts.length > slideCount && <CarouselButton onClick={scrollNext} direction="next" disabled={!canScrollNext}/>}
        </Carousel>
      </div>
    </>;
}
const CarouselViewport = React.forwardRef(({ children }, ref)=>{
    return <div className={`embla__viewport ${({
        className: "xb3r6kr x9ek82g x1azgikf"
    }).className}`} ref={ref}>
        {children}
      </div>;
});
const CarouselContainer = React.forwardRef(({ children }, ref)=>{
    return <div className={`embla__container ${({
        className: "x78zum5"
    }).className}`} ref={ref}>
        {children}
      </div>;
});
const Carousel = React.forwardRef(({ children }, ref)=>{
    return <div ref={ref} className={`embla ${({
        className: "x78zum5 x1a02dak xuyivp xl56j7k x1n6hpqp xou54vl xh8yej3"
    }).className}`}>
      {children}
    </div>;
});
const CarouselButton = ({ direction, onClick, disabled = false })=>{
    const intl = useIntl();
    const isRTL = useRTL();
    const isFlipped = direction === "next" && !isRTL || direction === "previous" && isRTL;
    return <button onClick={onClick} aria-label={direction === "next" ? intl.formatMessage(defineMessage({
        id: "Next related focus articles",
        defaultMessage: "Next related focus articles"
    })) : intl.formatMessage(defineMessage({
        id: "Previous related focus articles",
        defaultMessage: "Previous related focus articles"
    }))} {...{
        0: {
            className: "xjbqb8w xc342km x12klvby x1ypdohk xp08b3u xhr9w20 xo1ph6p x1hc1fzr x67bb7w"
        },
        2: {
            className: "xjbqb8w xc342km x12klvby x1ypdohk xp08b3u xhr9w20 x14yy4lh x1dqlzp4 x1hc1fzr x67bb7w"
        },
        1: {
            className: "xjbqb8w xc342km x12klvby x1ypdohk xp08b3u xhr9w20 xo1ph6p xbyyjgo x47corl"
        },
        3: {
            className: "xjbqb8w xc342km x12klvby x1ypdohk xp08b3u xhr9w20 x14yy4lh x1dqlzp4 xbyyjgo x47corl"
        }
    }[!!(direction === "previous") << 1 | !!disabled << 0]}>
      <ArrowLeft14 {...{
        0: {
            className: "xvy4d1p xxk0z11"
        },
        1: {
            className: "xvy4d1p xxk0z11 x19jd1h0"
        }
    }[!!isFlipped << 0]}/>
    </button>;
};
